import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { dashboardAPI } from '../services/api';

const Analytics = () => {
  const [tabValue, setTabValue] = useState(0);
  const [departmentData, setDepartmentData] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [topIssues, setTopIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        const [dept, trend, issues] = await Promise.all([
          dashboardAPI.getDepartmentAnalytics(),
          dashboardAPI.getMonthlyTrend(12),
          dashboardAPI.getCategoryAnalytics()
        ]);

        if (dept.data.success) setDepartmentData(dept.data.data.rows || []);
        if (trend.data.success) setMonthlyTrend(trend.data.data.rows || []);
        if (issues.data.success) setTopIssues(issues.data.data.rows.slice(0, 10) || []);
      } catch (err) {
        setError('Error loading analytics: ' + err.message);
        console.error('Analytics error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, px: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#1f2937',
            mb: 1
          }}
        >
          Analytics
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            fontSize: '0.95rem'
          }}
        >
          Department performance, complaint trends, and key metrics
        </Typography>
      </Box>

      {/* Tabs Section */}
      <Paper elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2, mx: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid #e5e7eb`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              color: '#6b7280',
              padding: '12px 20px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                color: '#667eea',
                bgcolor: '#f9fafb'
              }
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#667eea',
              fontWeight: 600
            },
            '& .MuiTabIndicator-root': {
              backgroundColor: '#667eea',
              height: 2
            }
          }}
        >
          <Tab label="Department Performance" />
          <Tab label="Monthly Trends" />
          <Tab label="Top Issues" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 2, px: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Department Performance Tab */}
              {tabValue === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Department Performance</Typography>
                  {departmentData.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolution Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Days Open</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {departmentData.map((row) => (
                          <TableRow key={row.dept_id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell>{row.department_name}</TableCell>
                            <TableCell align="right">{row.total_complaints}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>
                              {row.resolution_rate}%
                            </TableCell>
                            <TableCell align="right">{row.avg_days_open}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ color: '#9ca3af', textAlign: 'center', py: 3 }}>No data available</Typography>
                  )}
                </Box>
              )}

              {/* Monthly Trends Tab */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>12-Month Trends</Typography>
                  {monthlyTrend.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>In Progress</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyTrend.map((row, idx) => (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell>{row.month}</TableCell>
                            <TableCell align="right">{row.total_complaints}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right">{row.in_progress}</TableCell>
                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>
                              {row.resolution_rate}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ color: '#9ca3af', textAlign: 'center', py: 3 }}>No data available</Typography>
                  )}
                </Box>
              )}

              {/* Top Issues Tab */}
              {tabValue === 2 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Top 10 Complaint Categories</Typography>
                  {topIssues.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Resolved</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topIssues.map((row) => (
                          <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell>{row.category}</TableCell>
                            <TableCell align="right">{row.complaint_count}</TableCell>
                            <TableCell align="right">{row.resolved}</TableCell>
                            <TableCell align="right" sx={{ color: '#667eea', fontWeight: 600 }}>
                              {row.percentage}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ color: '#9ca3af', textAlign: 'center', py: 3 }}>No data available</Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Analytics;
